import React from 'react';
import PropTypes from 'prop-types';

/**
 * 통일된 버튼 컴포넌트
 * 다양한 크기와 스타일을 제공
 */
export const Button = ({ 
  children, 
  onClick,
  type = 'button',
  variant = 'primary', // 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark', 'ghost'
  size = 'default', // 'xs', 'sm', 'default', 'lg', 'xl'
  disabled = false,
  loading = false,
  icon = null,
  iconPosition = 'left', // 'left', 'right'
  className = '',
  fullWidth = false,
  rounded = '3xl', // 'none', 'sm', 'default', 'md', 'lg', 'xl', '2xl', '3xl', 'full'
  form = null, // form 속성 추가
  ...rest // 나머지 props를 받을 수 있도록 추가
}) => {
  // 크기별 클래스
  const sizeClasses = {
    xs: 'px-2 py-1 text-xs',
    sm: 'px-3 py-1.5 text-sm',
    default: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
    xl: 'px-8 py-4 text-lg'
  };

  // 라운드별 클래스
  const roundedClasses = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    default: 'rounded',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    '3xl': 'rounded-3xl',
    full: 'rounded-full'
  };

  // 변형별 클래스 - 디자인 토큰 사용
  const variantClasses = {
    primary: 'bg-primary text-white hover:bg-primary-hover focus:ring-primary',
    'primary-outline': 'bg-white text-primary hover:bg-gray-50 focus:ring-primary border border-primary',
    secondary: 'bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-gray-500',
    success: 'bg-success text-white hover:bg-success-hover focus:ring-success',
    danger: 'bg-danger text-white hover:bg-danger-hover focus:ring-danger',
    warning: 'bg-warning text-gray-900 hover:bg-warning-hover focus:ring-warning',
    info: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    light: 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500',
    dark: 'bg-gray-800 text-white hover:bg-gray-900 focus:ring-gray-700',
    ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-500 border border-gray-300'
  };

  // 기본 클래스
  const baseClasses = `font-medium ${roundedClasses[rounded]} focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200`;
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';
  const fullWidthClasses = fullWidth ? 'w-full' : '';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      form={form}
      className={`${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${disabledClasses} ${fullWidthClasses} ${className}`}
      {...rest}
    >
      <div className="flex items-center justify-center gap-1">
        {/* 왼쪽 아이콘 */}
        {icon && iconPosition === 'left' && (
          <span className="shrink-0">
            {icon}
          </span>
        )}
        
        {/* 로딩 스피너 */}
        {loading && (
          <div className="size-4 animate-spin rounded-full border-b-2 border-current"></div>
        )}
        
        {/* 버튼 텍스트와 오른쪽 아이콘을 하나로 합침 */}
        {children && (
          <span className={`flex items-center gap-1 whitespace-nowrap ${loading ? 'opacity-75' : ''}`}>
            {children}
            {icon && iconPosition === 'right' && (
              <span className="shrink-0">
                {icon}
              </span>
            )}
          </span>
        )}
        
        {/* 오른쪽 아이콘 (children이 없는 경우) */}
        {icon && iconPosition === 'right' && !children && (
          <span className="shrink-0">
            {icon}
          </span>
        )}
      </div>
    </button>
  );
};

/**
 * 아이콘 버튼 컴포넌트
 * 아이콘만 있는 원형 또는 사각형 버튼
 */
export const IconButton = ({ 
  children, 
  onClick,
  type = 'button',
  variant = 'light',
  size = 'default',
  disabled = false,
  className = '',
  rounded = true,
  ...rest
}) => {
  const sizeClasses = {
    xs: 'p-1',
    sm: 'p-1.5',
    default: 'p-2',
    lg: 'p-3',
    xl: 'p-4'
  };

  const variantClasses = {
    primary: 'bg-primary text-white hover:bg-primary-hover',
    secondary: 'bg-gray-200 text-gray-700 hover:bg-gray-300',
    success: 'bg-success text-white hover:bg-success-hover',
    danger: 'bg-danger text-white hover:bg-danger-hover',
    warning: 'bg-warning text-gray-900 hover:bg-warning-hover',
    info: 'bg-blue-600 text-white hover:bg-blue-700',
    light: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
    dark: 'bg-gray-800 text-white hover:bg-gray-900',
    ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 border border-gray-300'
  };

  const baseClasses = `transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 ${rounded ? 'rounded-full' : 'rounded'}`;
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${disabledClasses} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
};

Button.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  variant: PropTypes.oneOf(['primary', 'primary-outline', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark', 'ghost']),
  size: PropTypes.oneOf(['xs', 'sm', 'default', 'lg', 'xl']),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  className: PropTypes.string,
  fullWidth: PropTypes.bool,
  rounded: PropTypes.oneOf(['none', 'sm', 'default', 'md', 'lg', 'xl', '2xl', '3xl', 'full']),
  form: PropTypes.string
};

IconButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  variant: PropTypes.oneOf(['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark', 'ghost']),
  size: PropTypes.oneOf(['xs', 'sm', 'default', 'lg', 'xl']),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  rounded: PropTypes.bool
};
